import { db, COLLECTIONS } from '../firebase/db';
import { collection, query, onSnapshot, doc, updateDoc, deleteDoc, Unsubscribe } from 'firebase/firestore';
import { ScheduleWindow, DayLimitOverride } from './types';

export interface UseSchedulesState {
  schedules: ScheduleWindow[];
  loading: boolean;
  error: string | null;
}

export interface UseSchedulesActions {
  addSchedule: (schedule: Omit<ScheduleWindow, 'id'>) => Promise<void>;
  updateSchedule: (schedule: ScheduleWindow) => Promise<void>;
  deleteSchedule: (scheduleId: string) => Promise<void>;
  unsubscribe: () => void;
}

// Custom hook: useSchedules
export function useSchedules(familyId: string, deviceId: string): UseSchedulesState & UseSchedulesActions {
  const state: UseSchedulesState = {
    schedules: [],
    loading: true,
    error: null,
  };

  let unsubscribeFn: Unsubscribe | null = null;

  function subscribe() {
    try {
      const q = query(
        collection(db, COLLECTIONS.FAMILIES, familyId, 'devices', deviceId, 'schedules')
      );

      unsubscribeFn = onSnapshot(q, snapshot => {
        state.schedules = snapshot.docs.map(doc => ({
          ...(doc.data() as ScheduleWindow),
          id: doc.id,
        }));
        state.loading = false;
        state.error = null;
      }, err => {
        console.error('Failed to load schedules:', err);
        state.error = err.message;
        state.loading = false;
      });
    } catch (err: any) {
      state.error = err.message;
      state.loading = false;
    }
  }

  async function addSchedule(schedule: Omit<ScheduleWindow, 'id'>) {
    try {
      const id = Math.random().toString(36).substr(2, 9);
      const ref = doc(db, COLLECTIONS.FAMILIES, familyId, 'devices', deviceId, 'schedules', id);
      await updateDoc(ref, {
        ...schedule,
        createdAt: new Date(),
      });
    } catch (err: any) {
      throw new Error(`Failed to add schedule: ${err.message}`);
    }
  }

  async function updateSchedule(schedule: ScheduleWindow) {
    try {
      const ref = doc(db, COLLECTIONS.FAMILIES, familyId, 'devices', deviceId, 'schedules', schedule.id);
      await updateDoc(ref, {
        daysMask: schedule.daysMask,
        startMinute: schedule.startMinute,
        endMinute: schedule.endMinute,
        updatedAt: new Date(),
      });
    } catch (err: any) {
      throw new Error(`Failed to update schedule: ${err.message}`);
    }
  }

  async function deleteSchedule(scheduleId: string) {
    try {
      const ref = doc(db, COLLECTIONS.FAMILIES, familyId, 'devices', deviceId, 'schedules', scheduleId);
      await deleteDoc(ref);
    } catch (err: any) {
      throw new Error(`Failed to delete schedule: ${err.message}`);
    }
  }

  function unsubscribe() {
    if (unsubscribeFn) {
      unsubscribeFn();
      unsubscribeFn = null;
    }
  }

  subscribe();

  return {
    ...state,
    addSchedule,
    updateSchedule,
    deleteSchedule,
    unsubscribe,
  };
}

// Custom hook: useDayOverrides
export interface UseDayOverridesState {
  overrides: DayLimitOverride[];
  loading: boolean;
  error: string | null;
}

export interface UseDayOverridesActions {
  addOverride: (override: Omit<DayLimitOverride, 'id'>) => Promise<void>;
  updateOverride: (override: DayLimitOverride) => Promise<void>;
  deleteOverride: (overrideId: string) => Promise<void>;
  unsubscribe: () => void;
}

export function useDayOverrides(familyId: string, deviceId: string): UseDayOverridesState & UseDayOverridesActions {
  const state: UseDayOverridesState = {
    overrides: [],
    loading: true,
    error: null,
  };

  let unsubscribeFn: Unsubscribe | null = null;

  function subscribe() {
    try {
      const q = query(
        collection(db, COLLECTIONS.FAMILIES, familyId, 'devices', deviceId, 'dayOverrides')
      );

      unsubscribeFn = onSnapshot(q, snapshot => {
        state.overrides = snapshot.docs.map(doc => ({
          ...(doc.data() as DayLimitOverride),
          id: doc.id,
        }));
        state.loading = false;
        state.error = null;
      }, err => {
        console.error('Failed to load day overrides:', err);
        state.error = err.message;
        state.loading = false;
      });
    } catch (err: any) {
      state.error = err.message;
      state.loading = false;
    }
  }

  async function addOverride(override: Omit<DayLimitOverride, 'id'>) {
    try {
      const id = Math.random().toString(36).substr(2, 9);
      const ref = doc(db, COLLECTIONS.FAMILIES, familyId, 'devices', deviceId, 'dayOverrides', id);
      await updateDoc(ref, {
        ...override,
        createdAt: new Date(),
      });
    } catch (err: any) {
      throw new Error(`Failed to add day override: ${err.message}`);
    }
  }

  async function updateOverride(override: DayLimitOverride) {
    try {
      const ref = doc(db, COLLECTIONS.FAMILIES, familyId, 'devices', deviceId, 'dayOverrides', override.id);
      await updateDoc(ref, {
        dayOfWeek: override.dayOfWeek,
        limitMinutes: override.limitMinutes,
        updatedAt: new Date(),
      });
    } catch (err: any) {
      throw new Error(`Failed to update day override: ${err.message}`);
    }
  }

  async function deleteOverride(overrideId: string) {
    try {
      const ref = doc(db, COLLECTIONS.FAMILIES, familyId, 'devices', deviceId, 'dayOverrides', overrideId);
      await deleteDoc(ref);
    } catch (err: any) {
      throw new Error(`Failed to delete day override: ${err.message}`);
    }
  }

  function unsubscribe() {
    if (unsubscribeFn) {
      unsubscribeFn();
      unsubscribeFn = null;
    }
  }

  subscribe();

  return {
    ...state,
    addOverride,
    updateOverride,
    deleteOverride,
    unsubscribe,
  };
}
